import React, { useEffect, useState } from "react";
import {
  Button,
  FullscreenDialog,
  HSpacer,
  IconButton,
  Text,
  VSpacer,
} from "@/components/DesignSystem";
import { SponsorType } from "@shared/enums";
import { Stack } from "@mui/material";
import { ProgressBars } from "@/pages/Promotions/PromotionModal/ProgressBars";
import { WorkflowStep1 } from "@/pages/Promotions/PromotionModal/WorkflowStep1";
import { WorkflowStep2 } from "@/pages/Promotions/PromotionModal/WorkflowStep2";
import { WorkflowStep3 } from "@/pages/Promotions/PromotionModal/WorkflowStep3";
import { WorkflowStep4 } from "@/pages/Promotions/PromotionModal/WorkflowStep4";
import ArrowForward from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useGetRetailerById } from "@/hooks/useRetailer";
import { useGetRetailerLocations } from "@/hooks/useRetailerLocations";
import { SharedConfig } from "@shared/SharedConfig";
import { RewardsUom } from "@shared/enums/RewardsUom";
import { RewardsType } from "@shared/enums/RewardsType";
import { WorkflowStepBase } from "@/pages/Promotions/PromotionModal/WorkflowStepBase";
import {
  hasPromotionInputChanges,
  hasRequiredRedeemProducts,
  isEndDateInPast,
  isPointCapBelowPointsAwarded,
  isPointsExpirationBeforeEndDate,
  isPointsExpirationInPast,
  isPromotionDateRangeInverted,
  checkProgramPeriod,
  isProgramPeriodBlocking,
  toPromotionInputs,
} from "@/pages/Promotions/PromotionModal/Helpers";
import { businessToday } from "@shared/utilities";
import { useTranslation } from 'react-i18next';
import { useQuery } from "react-query";
import { QueryKeys } from "@/constants/QueryKeys";
import { RewardsProgramApi } from "@/utilities/api/RewardsProgramApi";
import { ApiPromotionWithPointsAwarded, ApiRetailer } from "@api/interfaces";
import { SubwalletApi } from "@/utilities/api/SubwalletApi";
import { isNil } from "lodash";

export type PromotionInputs = {
  allLocations?: boolean,
  description?: string,
  dollarCap: number | null,
  endDate?: string | null,
  id?: string,
  grantsPointsManually: boolean,
  locationIds?: string[],
  manufacturerId?: string,
  name?: string,
  overridePointsEngine: boolean,
  pointCap: number | null,
  pointsAwarded?: number,
  pointsExpirationDate?: string | null,
  productIdsForEarningPoints?: string[],
  productIdsForRedeemingPoints?: string[],
  productIdsWithPointsAwarded?: string[],
  redeemOnAllProducts?: boolean,
  retailerId?: string,
  rewardType: RewardsType,
  showInDashboardCarousel: boolean,
  showInDashboardTable: boolean,
  sponsorType: SponsorType,
  subwalletId?: string,
  startDate?: string | null,
  uom?: RewardsUom,
  value?: number,
}

interface PromotionModalProps {
  isSaving: boolean,
  onClose: () => void,
  onSave: (promotion: PromotionInputs) => Promise<void>,
  preSelectedRetailer?: ApiRetailer,
  promotionToEdit?: ApiPromotionWithPointsAwarded,
  show: boolean,
}

const initialPromotionState: PromotionInputs = {
  dollarCap: null,
  grantsPointsManually: false,
  manufacturerId: '',
  overridePointsEngine: false,
  pointCap: null,
  rewardType: RewardsType.Dollars,
  showInDashboardCarousel: false,
  showInDashboardTable: false,
  sponsorType: SponsorType.Retailer,
  uom: RewardsUom.Dollars,
};

export const PromotionModal = ({
  isSaving,
  onClose,
  onSave,
  preSelectedRetailer,
  promotionToEdit,
  show,
}: PromotionModalProps) => {
  const { t } = useTranslation();
  const isEditMode = !!promotionToEdit;
  const storedPointCap = promotionToEdit?.pointCap;
  const canEditExpirationDate = !promotionToEdit
    || promotionToEdit.pointsExpirationDate > businessToday();
  const [currentWorkflowStep, setCurrentWorkflowStep] = useState(0);
  const [progress, setProgress] = useState([0, 0, 0, 0]);
  const [promotion, setPromotion] = useState<PromotionInputs>(
    promotionToEdit ? toPromotionInputs(promotionToEdit) : {
      ...initialPromotionState,
      ...(preSelectedRetailer ? { retailerId: preSelectedRetailer.id } : {}),
    },
  );

  const hasChanges = !promotionToEdit
    || hasPromotionInputChanges(promotion, toPromotionInputs(promotionToEdit));

  const workflowStepRequiredFields : Record<number, (keyof PromotionInputs)[]> = {
    0: [
      'endDate',
      'manufacturerId',
      'name',
      'pointsExpirationDate',
      'redeemOnAllProducts',
      'retailerId',
      'sponsorType',
      'startDate',
    ],
    1: ['uom', 'value'],
    2: ['productIdsForEarningPoints'],
    3: ['productIdsForRedeemingPoints'],
    4: [],
  };

  workflowStepRequiredFields[0].push('subwalletId');

  const totalWorkflowSteps = Object.keys(workflowStepRequiredFields).length;

  const { retailer: selectedRetailer } = useGetRetailerById(
    promotion.retailerId || preSelectedRetailer?.id || '',
  );

  const { data: rewardsProgram } = useQuery(
    [QueryKeys.GET_ACTIVE_PROGRAM_FOR_RETAILER, selectedRetailer?.id],
    () => RewardsProgramApi.getActiveProgramForRetailer(selectedRetailer?.id ?? ''),
    { enabled: !!selectedRetailer?.id },
  );

  const isSelectedRetailerLoaded = !!selectedRetailer
    && selectedRetailer.id === promotion.retailerId;
  const { data: activeOrFuturePrograms } = useQuery(
    [QueryKeys.LIST_REWARDS_PROGRAMS, selectedRetailer?.id, 'activeOrFuture'],
    () => RewardsProgramApi.list({
      isExpired: false,
      minimal: true,
      retailerId: selectedRetailer?.id ?? '',
    }),
    { enabled: isSelectedRetailerLoaded },
  );

  const programPeriodCheck = checkProgramPeriod({
    isProgramCreationDisabled: selectedRetailer?.isProgramCreationDisabled,
    programs: isSelectedRetailerLoaded ? activeOrFuturePrograms : undefined,
    promotion,
    storedEndDate: promotionToEdit?.endDate,
  });

  const { retailerLocations: retailerLocationsResponse } = useGetRetailerLocations(
    promotion.retailerId ?? '',
    { limit: SharedConfig.maxPageLimit },
  );

  const { data: subwallets } = useQuery(
    [QueryKeys.LIST_SUBWALLETS, selectedRetailer?.id],
    () => selectedRetailer && SubwalletApi.list({ retailerId: selectedRetailer.id }),
    { enabled: !!selectedRetailer },
  );

  const hasInvalidDates = isPromotionDateRangeInverted(promotion)
    || (isEditMode && isEndDateInPast(promotion))
    || isPointsExpirationBeforeEndDate(promotion)
    || isPointsExpirationInPast(promotion)
    || isProgramPeriodBlocking(programPeriodCheck);

  const isStepComplete = (currentStep: number) => {
    const requiredFields = workflowStepRequiredFields[currentStep];
    for (const input of requiredFields) {
      const isValid = !isNil(promotion[input]);
      if (!isValid) {
        return false;
      }
    }
    if (currentStep === 0 && !!retailerLocationsResponse) {
      if (!promotion.allLocations && !promotion.locationIds?.length) {
        return false;
      }
    }
    if (currentStep === 0 && hasInvalidDates) {
      return false;
    }
    if (currentStep === 1 && isPointCapBelowPointsAwarded(promotion, storedPointCap)) {
      return false;
    }
    if (currentStep === 3 && !hasRequiredRedeemProducts(promotion, isEditMode)) {
      return false;
    }
    return true;
  };

  const areAllStepsComplete = () => {
    for (let i = 0; i < totalWorkflowSteps; i++) {
      if (!isStepComplete(i)) {
        return false;
      }
    }
    return true;
  };

  const updateProgress = () => {
    const updatedStepProgress = isStepComplete(currentWorkflowStep) ? 100 : 10;
    let updatedProgress = progress;
    if (currentWorkflowStep === 0) {
      updatedProgress = [updatedStepProgress, 0, 0, 0];
    } else if (currentWorkflowStep === 1) {
      updatedProgress = [100, updatedStepProgress, 0, 0];
    } else if (currentWorkflowStep === 2) {
      updatedProgress = [100, 100, isStepComplete(currentWorkflowStep) ? 50 : 10, 0];
    } else if (currentWorkflowStep === 3) {
      updatedProgress = [100, 100, isStepComplete(currentWorkflowStep) ? 100 : 50, 0];
    } else if (currentWorkflowStep === 4) {
      updatedProgress = [100, 100, 100, 90];
    }
    setProgress(updatedProgress);
  };

  const onPromotionChanged = (promotion: PromotionInputs) => {
    setPromotion(promotion);
  };

  const navigateToStep = (step: number) => {
    setCurrentWorkflowStep(step);
  };

  useEffect(() => {
    if (retailerLocationsResponse && !isEditMode) {
      onPromotionChanged({ ...promotion, allLocations: true, locationIds: undefined });
    }
  }, [retailerLocationsResponse, isEditMode]);

  useEffect(() => {
    updateProgress();
  }, [currentWorkflowStep, promotion]);

  return (
    <FullscreenDialog
      dialogActions={
        <Stack
          alignItems="center"
          direction="row"
          height={"80px"}
          justifyContent="space-between"
          sx={{ margin: "0 auto" }}
          width={"800px"}
        >
          <Button
            onClick={onClose}
            testID="create-promotion-modal-button-cancel-id"
            variant={"outlined"}
          >
            {isEditMode
              ? t($ => $.promotions.edit.discardChanges)
              : t($ => $.common.actions.cancel)}
          </Button>
          <Stack direction="row" justifyContent="space-between">
            {currentWorkflowStep > 0 && (
              <Stack direction="row">
                <Button
                  onClick={() => {
                    if (currentWorkflowStep === 4 && promotion.redeemOnAllProducts) {
                      navigateToStep(2);
                    } else {
                      navigateToStep(currentWorkflowStep - 1);
                    }
                  }}
                  startIcon={<ArrowBackIcon />}
                  testID="create-promotion-modal-button-back-id"
                  variant={'outlined'}
                >
                  {t($ => $.common.actions.back)}
                </Button>
                <HSpacer size="5" />
              </Stack>
            )}
            {currentWorkflowStep < totalWorkflowSteps - 1 && (
              <Button
                disabled={!isStepComplete(currentWorkflowStep)}
                endIcon={<ArrowForward />}
                onClick={() => {
                  if (currentWorkflowStep === 2 && promotion.redeemOnAllProducts) {
                    onPromotionChanged({
                      ...promotion,
                      productIdsForRedeemingPoints: [],
                    });
                    navigateToStep(4);
                  } else {
                    navigateToStep(currentWorkflowStep + 1);
                  }
                }}
                testID="create-promotion-modal-button-next-id"
              >
                {t($ => $.common.actions.next)}
              </Button>
            )}
            {currentWorkflowStep === totalWorkflowSteps - 1 && (
              <Button
                disabled={!areAllStepsComplete() || isSaving || !hasChanges}
                onClick={async () => {
                  await onSave(promotion);
                }}
                testID="create-promotion-modal-button-save-id"
              >
                {isEditMode
                  ? t($ => $.promotions.edit.saveChanges)
                  : t($ => $.promotions.create.step4.createPromotion)}
              </Button>
            )}
          </Stack>
        </Stack>
      }
      hideCloseButton={true}
      onClose={onClose}
      open={show}
      testID="create-promotion-modal-id"
      titleComponent={
        <Stack sx={{ width: '800px' }}>
          <VSpacer size="4" />
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="center"
            position="relative"
          >
            {isEditMode && (
              <IconButton
                onClick={onClose}
                sx={{ left: 0, position: 'absolute' }}
                testID="edit-promotion-modal-button-back-id"
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            <Text category="overline">
              {isEditMode
                ? t($ => $.promotions.edit.title)
                : t($ => $.promotions.create.title)}
            </Text>
          </Stack>
          <VSpacer size="5" />
          <ProgressBars progress={progress}/>
          <VSpacer size="4" />
        </Stack>
      }
      titleStyle={{ margin: '0 auto', width: '800px' }}
    >
      <Stack sx={{ margin: '0 auto', maxWidth: '800px' }}>
        <VSpacer size="6" />
        {currentWorkflowStep === 0 && (
          <WorkflowStepBase title={t($ => $.promotions.create.step1.title)}>
            <WorkflowStep1
              activeRewardsProgram={rewardsProgram}
              canEditExpirationDate={canEditExpirationDate}
              isAllLocationsLocked={isEditMode && promotionToEdit?.allLocations}
              isEditMode={isEditMode}
              locationIdsWithPointsAwarded={promotionToEdit?.locationIdsWithPointsAwarded}
              onPromotionChanged={onPromotionChanged}
              programPeriodCheck={programPeriodCheck}
              promotion={promotion}
              retailerLocations={retailerLocationsResponse?.data}
              selectedRetailer={selectedRetailer}
              subwallets={subwallets}
            />
          </WorkflowStepBase>
        )}
        {currentWorkflowStep === 1 && (
          <WorkflowStepBase title={t($ => $.promotions.create.step2.title)}>
            <WorkflowStep2
              isEditMode={isEditMode}
              onPromotionChanged={onPromotionChanged}
              promotion={promotion}
              storedPointCap={storedPointCap}
            />
          </WorkflowStepBase>
        )}
        {currentWorkflowStep === 2 && selectedRetailer && (
          <WorkflowStepBase
            title={t($ => $.promotions.create.step3.earnTitle)}
            width="840px"
          >
            <WorkflowStep3
              onPromotionChanged={onPromotionChanged}
              productIdsWithPointsAwarded={promotionToEdit?.productIdsWithPointsAwarded}
              productType="earn"
              promotion={promotion}
              selectedRetailer={selectedRetailer}
            />
          </WorkflowStepBase>
        )}
        {currentWorkflowStep === 3 && selectedRetailer && (
          <WorkflowStepBase
            title={t($ => $.promotions.create.step3.redeemTitle)}
            width="840px"
          >
            <WorkflowStep3
              onPromotionChanged={onPromotionChanged}
              productType="redeem"
              promotion={promotion}
              selectedRetailer={selectedRetailer}
            />
          </WorkflowStepBase>
        )}
        {currentWorkflowStep === 4 && selectedRetailer && (
          <WorkflowStepBase title={t($ => $.promotions.create.step4.title)}>
            <WorkflowStep4
              activeRewardsProgram={rewardsProgram}
              canEditExpirationDate={canEditExpirationDate}
              isEditMode={isEditMode}
              navigateToStep={navigateToStep}
              onPromotionChanged={onPromotionChanged}
              promotion={promotion}
              retailerLocations={retailerLocationsResponse?.data}
              selectedRetailer={selectedRetailer}
              subwallets={subwallets}
            />
          </WorkflowStepBase>
        )}
      </Stack>
    </FullscreenDialog>
  );
};
